import { useMemo } from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { lighten } from '@mui/material/styles';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { formatDistanceToNowStrict } from 'date-fns';
import { RotateCw } from 'lucide-react';
import { useSnackbar } from 'notistack';

import { isSignedIn, useAuth } from '@/features/scaffolding/components/AuthContext';
import { Loading } from '@/shared/components';
import AppPage from '@/shared/components/PageComponents/AppPage';
import PageHeader from '@/shared/components/PageHeader';
import StatusBadge from '@/shared/components/StatusBadge';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';
import { cx } from '@/styles/muiTheme';

import useCronJobsQuery, { CronJob } from '../hooks/useCronJobsQuery';
import useRestartCronMutation from '../hooks/useRestartCronMutation';

/**
 * One hue per queue, so rows group by queue without reading the label.
 *
 * Lightened off the palette hues for the reason StatusBadge documents — raw
 * blue.hover is only 3.9:1 on a surface row, under the 4.5:1 needed for body
 * text. Green and red are deliberately left alone; the Status column owns them.
 */
const QUEUE_COLORS: Record<string, string> = {
  MainQueue: lighten(cx.blue.hover, 0.25),
  GameMonitoringQueue: cx.gold,
};

/** Falls back to plain body text for any queue added without a hue here. */
const QueueCell = ({ queue }: { queue: string }) => (
  <Box component="span" sx={{ color: QUEUE_COLORS[queue] ?? cx.text.secondary, fontWeight: 500 }}>
    {queue}
  </Box>
);

/** Epoch ms -> "in 45s". Null while a cron is unscheduled, which is the point. */
const NextRunCell = ({ next }: { next: number | null }) => {
  if (!next) return <Box component="span" sx={{ color: cx.text.muted }}>—</Box>;

  return (
    <Box component="span" title={new Date(next).toLocaleString()}>
      in {formatDistanceToNowStrict(new Date(next))}
    </Box>
  );
};

const ManageCronJobsPage = () => {
  const title = 'Cron Jobs';

  useDocumentTitle(title);

  const { sessionState } = useAuth();
  const isAuthReady = isSignedIn(sessionState);

  const cronJobsQuery = useCronJobsQuery();
  const restartMutation = useRestartCronMutation();
  const snackBar = useSnackbar();

  const restart = (cron: CronJob) => {
    restartMutation.mutate(cron.id, {
      onSuccess: () =>
        snackBar.enqueueSnackbar(`Restart queued for ${cron.name}`, {
          variant: 'success',
          autoHideDuration: 3000,
        }),
      onError: () =>
        snackBar.enqueueSnackbar(`Failed to restart ${cron.name}`, {
          variant: 'error',
          autoHideDuration: 3000,
        }),
    });
  };

  const columns = useMemo<MRT_ColumnDef<CronJob>[]>(
    () => [
      { accessorKey: 'name', header: 'Name', size: 200 },
      {
        accessorKey: 'jobName',
        header: 'Job Name',
        size: 200,
        // Machine identifiers, same treatment as session handlers elsewhere.
        muiTableBodyCellProps: { sx: { fontFamily: cx.fontMono, color: cx.text.tertiary } },
      },
      {
        accessorKey: 'queue',
        header: 'Queue',
        size: 160,
        Cell: ({ row }) => <QueueCell queue={row.original.queue} />,
      },
      { accessorKey: 'intervalLabel', header: 'Interval', size: 200 },
      {
        id: 'status',
        header: 'Status',
        size: 130,
        accessorFn: (row) => (row.isScheduled ? 'Scheduled' : 'Missing'),
        Cell: ({ row }) => (
          <StatusBadge
            label={row.original.isScheduled ? 'Scheduled' : 'Missing'}
            color={row.original.isScheduled ? cx.green : cx.red.main}
          />
        ),
      },
      {
        id: 'next',
        header: 'Next Run',
        size: 140,
        enableSorting: false,
        Cell: ({ row }) => <NextRunCell next={row.original.next} />,
      },
      {
        id: 'actions',
        header: '',
        size: 120,
        enableSorting: false,
        Cell: ({ row }) => (
          <Button
            size="small"
            startIcon={<RotateCw size={14} />}
            onClick={() => restart(row.original)}
            // Only the row being restarted is disabled; the others stay usable.
            disabled={restartMutation.isPending && restartMutation.variables === row.original.id}
          >
            Restart
          </Button>
        ),
      },
    ],
    [restartMutation.isPending, restartMutation.variables],
  );

  if (!isAuthReady || cronJobsQuery.isLoading) {
    return (
      <AppPage title={title} requiredPermissions={['menu:admin:crons']}>
        <Loading />
      </AppPage>
    );
  }

  return (
    <AppPage title={title} requiredPermissions={['menu:admin:crons']}>
      <Container maxWidth="xl" sx={{ marginTop: 4, marginBottom: 4 }}>
        <PageHeader
          title={title}
          subtitle="Live status of the scheduler's repeatable jobs in BullMQ."
          action={
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ color: cx.text.muted }}>
                Auto-refreshing every 10s
              </Typography>
              <Button
                size="small"
                startIcon={<RotateCw size={14} />}
                onClick={() => cronJobsQuery.refetch()}
              >
                Refresh
              </Button>
            </Stack>
          }
        />

        <MaterialReactTable
          columns={columns}
          data={cronJobsQuery.data ?? []}
          enablePagination={false}
          enableBottomToolbar={false}
          state={{ showProgressBars: cronJobsQuery.isFetching }}
        />
      </Container>
    </AppPage>
  );
};

export default ManageCronJobsPage;
